import React, { useState, useEffect, useRef } from "react";
import Feed from "@models/feed";
import Product, { ProductType } from "@models/product";
import styles from "./HeroBannerSlider.module.scss";
import { useNavigate } from "react-router";
import { generateProductViewUrl } from "@utils/urlUtils";
import { cartStore, authStore } from "@stores/main";
import { useBreadcrumbNavigation } from "@utils/breadcrumbNavigation";
import { formatDiscountPercentage } from "@utils/formatDiscountPercentage";
import { gtagService } from "../../services/gtag";
import { pageTrackingService } from "../../services/pageTracking";

interface HeroBannerSliderProps {
  feed: Feed;
}

const HeroBannerSlider: React.FC<HeroBannerSliderProps> = ({ feed }) => {
  const navigate = useNavigate();
  const { goToLogin } = useBreadcrumbNavigation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isAddingToCart, setIsAddingToCart] = useState<string | null>(null);
  // Track which slides are showing video (after 2s poster display)
  const [showVideo, setShowVideo] = useState<{ [key: number]: boolean }>({});
  const videoTimeoutRefs = useRef<{ [key: number]: NodeJS.Timeout }>({});

  const items = feed.items || [];
  const totalItems = items.length;

  // Auto-play slider
  useEffect(() => {
    if (isAutoPlaying && totalItems > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % totalItems);
      }, 5000); // Change slide every 5 seconds
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAutoPlaying, totalItems]);

  // Pause auto-play on hover
  const handleMouseEnter = () => {
    setIsAutoPlaying(false);
  };

  const handleMouseLeave = () => {
    setIsAutoPlaying(true);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    // Resume auto-play after 10 seconds
    setTimeout(() => {
      setIsAutoPlaying(true);
    }, 10000);
  };

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + totalItems) % totalItems);
    setIsAutoPlaying(false);
    setTimeout(() => {
      setIsAutoPlaying(true);
    }, 10000);
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % totalItems);
    setIsAutoPlaying(false);
    setTimeout(() => {
      setIsAutoPlaying(true);
    }, 10000);
  };

  const handleProductClick = (product: Product) => {
    navigate(generateProductViewUrl(product));
  };

  const handleAddToCart = async (e: React.MouseEvent, product: Product) => {
    e.stopPropagation();
    e.preventDefault();

    if (isAddingToCart || cartStore.loading) {
      return;
    }

    if (!authStore.isAuthenticated) {
      goToLogin();
      return;
    }

    setIsAddingToCart(product.id);
    try {
      const res = await cartStore.addItemToCart(product.id);
      if (!res) {
        return;
      }

      pageTrackingService.trackProductAddToCart(product.id, product.title, 'hero_banner');

      gtagService.trackAddToCart({
        currency: product.currency || "USD",
        value: product.buyPrice,
        items: [
          {
            item_id: product.id,
            item_name: product.title,
            price: product.buyPrice,
            quantity: 1,
          },
        ],
      });
    } catch (error) {
      console.error("Error adding to cart:", error);
    } finally {
      setTimeout(() => {
        setIsAddingToCart(null);
      }, 300);
    }
  };

  // Handle feature banner video/poster timing
  useEffect(() => {
    items.forEach((product, index) => {
      if ((product.type as string) === ProductType.FeatureBanner || (product.type as string) === "feature_banner") {
        // Clear any existing timeout for this index
        if (videoTimeoutRefs.current[index]) {
          clearTimeout(videoTimeoutRefs.current[index]);
        }
        
        // Reset showVideo when slide changes
        if (index === currentIndex) {
          setShowVideo((prev) => ({ ...prev, [index]: false }));
          
          // Show poster for 2 seconds, then show video if available
          videoTimeoutRefs.current[index] = setTimeout(() => {
            setShowVideo((prev) => ({ ...prev, [index]: true }));
          }, 2000);
        } else {
          // Reset video state for non-active slides
          setShowVideo((prev) => ({ ...prev, [index]: false }));
        }
      }
    });

    return () => {
      // Cleanup timeouts
      Object.values(videoTimeoutRefs.current).forEach((timeout) => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, [currentIndex, items]);

  const handleRedirect = (url: string | null) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const isFeatureBanner = (product: Product) => {
    return (product.type as string) === ProductType.FeatureBanner || (product.type as string) === "feature_banner";
  };

  if (!items || items.length === 0) {
    return null;
  }

  const currentProduct = items[currentIndex];

  // Helper to construct full URL
  const getFullUrl = (url: string | null): string | null => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `${process.env.PUBLIC_URL}${url.startsWith('/') ? url : `/${url}`}`;
  };

  // Render feature banner slide for desktop
  const renderFeatureBannerDesktop = (product: Product, index: number) => {
    const shouldShowVideo = showVideo[index] && product.videoHeroBanner16_9;
    const posterUrl = getFullUrl(product.posterHeroBanner16_9);
    const videoUrl = getFullUrl(product.videoHeroBanner16_9);
    const hasMedia = (shouldShowVideo && videoUrl) || !!posterUrl;

    return (
      <div className={styles["feature-banner-content"]}>
        <div className={styles["feature-banner-media"]}>
          {shouldShowVideo && videoUrl ? (
            <video
              className={styles["feature-banner-video"]}
              autoPlay
              loop
              muted
              playsInline
            >
              <source src={videoUrl} type="video/mp4" />
            </video>
          ) : posterUrl ? (
            <img
              src={posterUrl}
              alt={product.title}
              className={styles["feature-banner-poster"]}
            />
          ) : null}
        </div>
        <div className={`${styles["feature-banner-overlay"]} ${hasMedia ? styles["feature-banner-overlay-transparent"] : ""}`}>
          <div className={styles["feature-banner-text"]}>
            {product.title && (
              <h2 className={styles["feature-banner-title"]}>
                {product.title}
              </h2>
            )}
            {product.description && (
              <p className={styles["feature-banner-description"]}>
                {product.description}
              </p>
            )}
            <div className={styles["feature-banner-cta"]}>
              {product.primaryCta && product.redirectTo && (
                <button
                  className={styles["feature-banner-primary-button"]}
                  onClick={() => handleRedirect(product.redirectTo)}
                >
                  {product.primaryCta}
                </button>
              )}
              {product.secondaryCta && product.secondaryRedirectTo && (
                <button
                  className={styles["feature-banner-secondary-button"]}
                  onClick={() => handleRedirect(product.secondaryRedirectTo)}
                >
                  {product.secondaryCta}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render feature banner slide for tablet/mobile
  const renderFeatureBannerTabletMobile = (product: Product, index: number) => {
    const shouldShowVideo = showVideo[index] && product.videoHeroBanner1_1;
    const posterUrl = getFullUrl(product.posterHeroBanner1_1);
    const videoUrl = getFullUrl(product.videoHeroBanner1_1);
    const hasMedia = (shouldShowVideo && videoUrl) || !!posterUrl;

    return (
      <div className={styles["feature-banner-content"]}>
        <div className={styles["feature-banner-media"]}>
          {shouldShowVideo && videoUrl ? (
            <video
              className={styles["feature-banner-video"]}
              autoPlay
              loop
              muted
              playsInline
            >
              <source src={videoUrl} type="video/mp4" />
            </video>
          ) : posterUrl ? (
            <img
              src={posterUrl}
              alt={product.title}
              className={styles["feature-banner-poster"]}
            />
          ) : null}
        </div>
        <div className={`${styles["feature-banner-overlay"]} ${hasMedia ? styles["feature-banner-overlay-transparent"] : ""}`}>
          <div className={styles["feature-banner-text"]}>
            {product.title && (
              <h2 className={styles["feature-banner-title"]}>
                {product.title}
              </h2>
            )}
            {product.description && (
              <p className={styles["feature-banner-description"]}>
                {product.description}
              </p>
            )}
            <div className={styles["feature-banner-cta"]}>
              {product.primaryCta && product.redirectTo && (
                <button
                  className={styles["feature-banner-primary-button"]}
                  onClick={() => handleRedirect(product.redirectTo)}
                >
                  {product.primaryCta}
                </button>
              )}
              {product.secondaryCta && product.secondaryRedirectTo && (
                <button
                  className={styles["feature-banner-secondary-button"]}
                  onClick={() => handleRedirect(product.secondaryRedirectTo)}
                >
                  {product.secondaryCta}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Set background images based on screen size
  const desktopBgImage = `${process.env.PUBLIC_URL}/images/banner/Store.png`;
  const tabletBgImage = `${process.env.PUBLIC_URL}/images/banner/HeroFeedTab.png`;
  const mobileBgImage = `${process.env.PUBLIC_URL}/images/banner/HeroFeedMobile.png`;

  return (
    <div
      className={styles["hero-banner-slider"]}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Desktop Hero Banner */}
      <div 
        className={`${styles["hero-banner-desktop"]} is-hidden-touch`}
        style={{ "--store-bg-image": `url(${desktopBgImage})` } as React.CSSProperties}
      >
        <div className={`${styles["slider-container"]} ${(() => {
          const currentProduct = items[currentIndex];
          if (!currentProduct) return false;
          const isFeature = isFeatureBanner(currentProduct);
          return isFeature && ((showVideo[currentIndex] && currentProduct.videoHeroBanner16_9) || !!currentProduct.posterHeroBanner16_9);
        })() ? styles["slider-container-feature"] : ""}`}>
          <div
            className={styles["slider-wrapper"]}
            style={{
              transform: `translateX(-${currentIndex * 100}%)`,
            }}
          >
            {items.map((product, index) => {
              const isFeature = isFeatureBanner(product);
              const hasFeatureMedia = isFeature && (
                (showVideo[index] && product.videoHeroBanner16_9) || 
                !!product.posterHeroBanner16_9
              );
              
              return (
              <div
                key={product.id}
                className={`${styles["slide"]} ${hasFeatureMedia ? styles["slide-feature-with-media"] : ""}`}
                style={{
                  "--slide-bg-image": product.background
                    ? `url(${product.background})`
                    : "none",
                } as React.CSSProperties}
              >
                {isFeature ? (
                  renderFeatureBannerDesktop(product, index)
                ) : (
                  <div className={styles["slide-content"]}>
                    <div className={styles["product-info"]}>
                      <div className={styles["product-poster"]}>
                        <img
                          src={
                            product.poster ||
                            `${process.env.PUBLIC_URL}/images/store-desktop-thumbnail.png`
                          }
                          alt={product.title}
                          onError={(e) => {
                            const target = e.currentTarget as HTMLImageElement;
                            target.onerror = null;
                            target.src = `${process.env.PUBLIC_URL}/images/store-desktop-thumbnail.png`;
                          }}
                        />
                        {!!product.discountPercentage && (
                          <>
                            <div className={`position-absolute ${styles["discount-tag-container"]}`}>
                              <img
                                src={
                                  process.env.PUBLIC_URL +
                                  "/images/tags/" +
                                  (product.hotOffer
                                    ? "discount-purple.svg"
                                    : "discount-orange.svg")
                                }
                                alt="Discount"
                                className={`${styles["discount-tag-img"]} ${product.discountPercentage > 99 ? styles["discount-tag-img-large"] : ''}`}
                              />
                            </div>
                            <div className={`${styles["discount-tag"]} ${product.discountPercentage > 99 ? styles["discount-tag-large"] : ''}`}>
                              <span className={`${product.hotOffer ? styles["discount-hotoffer"] : styles["discount-offered"]} is-flex is-align-items-start is-flex-direction-column fs-20`}>
                                {formatDiscountPercentage(product.discountPercentage)}%
                                <span className={styles["discount-off"] + " fs-14 fw-700"}>OFF</span>
                              </span>
                              {product.hotOffer && (
                                <div className={styles["limited-offer-tag"] + " position-absolute"}>
                                  <img
                                    src={
                                      process.env.PUBLIC_URL +
                                      "/images/tags/limited-offer-tag.svg"
                                    }
                                    alt="Limited Offer"
                                  />
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                      <div className={styles["product-details"]}>
                        <h2 className={styles["product-title"]}>
                          {product.title}
                        </h2>
                        {product.description && (
                          <p className={styles["product-description"]}>
                            {product.description.length > 200
                              ? `${product.description.substring(0, 200)}...`
                              : product.description}
                          </p>
                        )}
                        <div className={styles["product-price-section"]}>
                          <div className={styles["price-row"]}>
                            <span className={styles["current-price"]}>
                              {product.buyPriceString}
                            </span>
                            {product.displayPrice > product.buyPrice && (
                              <span className={styles["original-price"]}>
                                {product.displayPriceString}
                              </span>
                            )}
                          </div>
                          <div className={styles["action-buttons"]}>
                            <button
                              className={styles["buy-button"]}
                              onClick={() => handleProductClick(product)}
                            >
                              View Details
                            </button>
                            {!product.isSoldOut && (
                              <button
                                className={styles["cart-button"]}
                                onClick={(e) => handleAddToCart(e, product)}
                                disabled={
                                  isAddingToCart === product.id ||
                                  cartStore.loading ||
                                  cartStore.items.some(
                                    (item) => item.id === product.id
                                  )
                                }
                              >
                                {cartStore.items.some(
                                  (item) => item.id === product.id
                                ) || isAddingToCart === product.id ? (
                                  <img
                                    src={`${process.env.PUBLIC_URL}/images/icons/added-to-cart.svg`}
                                    alt="Added to Cart"
                                    width={24}
                                    height={24}
                                  />
                                ) : (
                                  <img
                                    src={`${process.env.PUBLIC_URL}/images/icons/add-to-cart.svg`}
                                    alt="Add to Cart"
                                    width={24}
                                    height={24}
                                  />
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              );
            })}
          </div>

          {/* Navigation Arrows */}
          {totalItems > 1 && (
            <>
              <button
                className={styles["nav-arrow"] + " " + styles["nav-arrow-left"]}
                onClick={goToPrevious}
                aria-label="Previous slide"
              >
                <img
                  src={`${process.env.PUBLIC_URL}/images/logos/arrow-left.svg`}
                  alt="Previous"
                  height="30"
                />
              </button>
              <button
                className={styles["nav-arrow"] + " " + styles["nav-arrow-right"]}
                onClick={goToNext}
                aria-label="Next slide"
              >
                <img
                  src={`${process.env.PUBLIC_URL}/images/logos/arrow-right.svg`}
                  alt="Next"
                  height="30"
                />
              </button>
            </>
          )}

          {/* Dots Indicator */}
          {totalItems > 1 && (
            <div className={styles["dots-indicator"]}>
              {items.map((_, index) => (
                <button
                  key={index}
                  className={`${styles["dot"]} ${
                    index === currentIndex ? styles["dot-active"] : ""
                  }`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tablet Hero Banner */}
      <div
        className={`${styles["hero-banner-tablet"]} is-hidden-mobile is-hidden-desktop`}
        style={{ "--store-bg-image": `url(${tabletBgImage})` } as React.CSSProperties}
      >
        <div className={`${styles["slider-container"]} ${(() => {
          const currentProduct = items[currentIndex];
          if (!currentProduct) return false;
          const isFeature = isFeatureBanner(currentProduct);
          return isFeature && ((showVideo[currentIndex] && currentProduct.videoHeroBanner1_1) || !!currentProduct.posterHeroBanner1_1);
        })() ? styles["slider-container-feature"] : ""}`}>
          <div
            className={styles["slider-wrapper"]}
            style={{
              transform: `translateX(-${currentIndex * 100}%)`,
            }}
          >
            {items.map((product, index) => {
              const isFeature = isFeatureBanner(product);
              const hasFeatureMedia = isFeature && (
                (showVideo[index] && product.videoHeroBanner1_1) || 
                !!product.posterHeroBanner1_1
              );
              
              return (
              <div
                key={product.id}
                className={`${styles["slide"]} ${hasFeatureMedia ? styles["slide-feature-with-media"] : ""}`}
                style={{
                  "--slide-bg-image": product.background
                    ? `url(${product.background})`
                    : "none",
                } as React.CSSProperties}
              >
                {isFeature ? (
                  renderFeatureBannerTabletMobile(product, index)
                ) : (
                  <div className={styles["slide-content"]}>
                    <div className={styles["product-info"]}>
                      <div className={styles["product-poster"]}>
                        <img
                          src={
                            product.poster ||
                            `${process.env.PUBLIC_URL}/images/store-tab-thumbnail.png`
                          }
                          alt={product.title}
                          onError={(e) => {
                            const target = e.currentTarget as HTMLImageElement;
                            target.onerror = null;
                            target.src = `${process.env.PUBLIC_URL}/images/store-tab-thumbnail.png`;
                          }}
                        />
                        {!!product.discountPercentage && (
                          <>
                            <div className={`position-absolute ${styles["discount-tag-container"]}`}>
                              <img
                                src={
                                  process.env.PUBLIC_URL +
                                  "/images/tags/" +
                                  (product.hotOffer
                                    ? "discount-purple.svg"
                                    : "discount-orange.svg")
                                }
                                alt="Discount"
                                className={`${styles["discount-tag-img"]} ${product.discountPercentage > 99 ? styles["discount-tag-img-large"] : ''}`}
                              />
                            </div>
                            <div className={`${styles["discount-tag"]} ${product.discountPercentage > 99 ? styles["discount-tag-large"] : ''}`}>
                              <span className={`${product.hotOffer ? styles["discount-hotoffer"] : styles["discount-offered"]} is-flex is-align-items-start is-flex-direction-column fs-20`}>
                                {formatDiscountPercentage(product.discountPercentage)}%
                                <span className={styles["discount-off"] + " fs-14 fw-700"}>OFF</span>
                              </span>
                              {product.hotOffer && (
                                <div className={styles["limited-offer-tag"] + " position-absolute"}>
                                  <img
                                    src={
                                      process.env.PUBLIC_URL +
                                      "/images/tags/limited-offer-tag.svg"
                                    }
                                    alt="Limited Offer"
                                  />
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                      <div className={styles["product-details"]}>
                        <h2 className={styles["product-title"]}>
                          {product.title}
                        </h2>
                        <div className={styles["product-price-section"]}>
                          <div className={styles["price-row"]}>
                            <span className={styles["current-price"]}>
                              {product.buyPriceString}
                            </span>
                            {product.displayPrice > product.buyPrice && (
                              <span className={styles["original-price"]}>
                                {product.displayPriceString}
                              </span>
                            )}
                          </div>
                          <div className={styles["action-buttons"]}>
                            <button
                              className={styles["buy-button"]}
                              onClick={() => handleProductClick(product)}
                            >
                              View Details
                            </button>
                            {!product.isSoldOut && (
                              <button
                                className={styles["cart-button"]}
                                onClick={(e) => handleAddToCart(e, product)}
                                disabled={
                                  isAddingToCart === product.id ||
                                  cartStore.loading ||
                                  cartStore.items.some(
                                    (item) => item.id === product.id
                                  )
                                }
                              >
                                {cartStore.items.some(
                                  (item) => item.id === product.id
                                ) || isAddingToCart === product.id ? (
                                  <img
                                    src={`${process.env.PUBLIC_URL}/images/icons/added-to-cart.svg`}
                                    alt="Added to Cart"
                                    width={20}
                                    height={20}
                                  />
                                ) : (
                                  <img
                                    src={`${process.env.PUBLIC_URL}/images/icons/add-to-cart.svg`}
                                    alt="Add to Cart"
                                    width={20}
                                    height={20}
                                  />
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              );
            })}
          </div>

          {totalItems > 1 && (
            <>
              <button
                className={styles["nav-arrow"] + " " + styles["nav-arrow-left"]}
                onClick={goToPrevious}
                aria-label="Previous slide"
              >
                <img
                  src={`${process.env.PUBLIC_URL}/images/logos/arrow-left.svg`}
                  alt="Previous"
                  height="24"
                />
              </button>
              <button
                className={styles["nav-arrow"] + " " + styles["nav-arrow-right"]}
                onClick={goToNext}
                aria-label="Next slide"
              >
                <img
                  src={`${process.env.PUBLIC_URL}/images/logos/arrow-right.svg`}
                  alt="Next"
                  height="24"
                />
              </button>
            </>
          )}

          {totalItems > 1 && (
            <div className={styles["dots-indicator"]}>
              {items.map((_, index) => (
                <button
                  key={index}
                  className={`${styles["dot"]} ${
                    index === currentIndex ? styles["dot-active"] : ""
                  }`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Mobile Hero Banner */}
      <div
        className={`${styles["hero-banner-mobile"]} is-hidden-tablet is-hidden-desktop`}
        style={{ "--store-bg-image": `url(${mobileBgImage})` } as React.CSSProperties}
      >
        <div className={`${styles["slider-container"]} ${(() => {
          const currentProduct = items[currentIndex];
          if (!currentProduct) return false;
          const isFeature = isFeatureBanner(currentProduct);
          return isFeature && ((showVideo[currentIndex] && currentProduct.videoHeroBanner1_1) || !!currentProduct.posterHeroBanner1_1);
        })() ? styles["slider-container-feature"] : ""}`}>
          <div
            className={styles["slider-wrapper"]}
            style={{
              transform: `translateX(-${currentIndex * 100}%)`,
            }}
          >
            {items.map((product, index) => {
              const isFeature = isFeatureBanner(product);
              const hasFeatureMedia = isFeature && (
                (showVideo[index] && product.videoHeroBanner1_1) || 
                !!product.posterHeroBanner1_1
              );
              
              return (
              <div
                key={product.id}
                className={`${styles["slide"]} ${hasFeatureMedia ? styles["slide-feature-with-media"] : ""}`}
                style={{
                  "--slide-bg-image": product.background
                    ? `url(${product.background})`
                    : "none",
                } as React.CSSProperties}
              >
                {isFeature ? (
                  renderFeatureBannerTabletMobile(product, index)
                ) : (
                  <div className={styles["slide-content"]}>
                    <div className={styles["product-poster"]}>
                      <img
                        src={
                          product.poster ||
                          `${process.env.PUBLIC_URL}/images/store-mobile-thumbnail.png`
                        }
                        alt={product.title}
                        onError={(e) => {
                          const target = e.currentTarget as HTMLImageElement;
                          target.onerror = null;
                          target.src = `${process.env.PUBLIC_URL}/images/store-mobile-thumbnail.png`;
                        }}
                      />
                      {!!product.discountPercentage && (
                        <>
                          <div className={`position-absolute ${styles["discount-tag-container"]}`}>
                            <img
                              src={
                                process.env.PUBLIC_URL +
                                "/images/tags/" +
                                (product.hotOffer
                                  ? "discount-purple.svg"
                                  : "discount-orange.svg")
                              }
                              alt="Discount"
                              className={`${styles["discount-tag-img"]} ${product.discountPercentage > 99 ? styles["discount-tag-img-large"] : ''}`}
                            />
                          </div>
                          <div className={`${styles["discount-tag"]} ${product.discountPercentage > 99 ? styles["discount-tag-large"] : ''}`}>
                            <span className={`${product.hotOffer ? styles["discount-hotoffer"] : styles["discount-offered"]} is-flex is-align-items-start is-flex-direction-column fs-20`}>
                              {formatDiscountPercentage(product.discountPercentage)}%
                              <span className={styles["discount-off"] + " fs-14 fw-700"}>OFF</span>
                            </span>
                            {product.hotOffer && (
                              <div className={styles["limited-offer-tag"] + " position-absolute"}>
                                <img
                                  src={
                                    process.env.PUBLIC_URL +
                                    "/images/tags/limited-offer-tag.svg"
                                  }
                                  alt="Limited Offer"
                                />
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                    <div className={styles["product-details"]}>
                      <h2 className={styles["product-title"]}>
                        {product.title}
                      </h2>
                      <div className={styles["product-price-section"]}>
                        <div className={styles["price-row"]}>
                          <span className={styles["current-price"]}>
                            {product.buyPriceString}
                          </span>
                          {product.displayPrice > product.buyPrice && (
                            <span className={styles["original-price"]}>
                              {product.displayPriceString}
                            </span>
                          )}
                        </div>
                        <div className={styles["action-buttons"]}>
                          <button
                            className={styles["buy-button"]}
                            onClick={() => handleProductClick(product)}
                          >
                            View Details
                          </button>
                          {!product.isSoldOut && (
                            <button
                              className={styles["cart-button"]}
                              onClick={(e) => handleAddToCart(e, product)}
                              disabled={
                                isAddingToCart === product.id ||
                                cartStore.loading ||
                                cartStore.items.some(
                                  (item) => item.id === product.id
                                )
                              }
                            >
                              {cartStore.items.some(
                                (item) => item.id === product.id
                              ) || isAddingToCart === product.id ? (
                                <img
                                  src={`${process.env.PUBLIC_URL}/images/icons/added-to-cart.svg`}
                                  alt="Added to Cart"
                                  width={20}
                                  height={20}
                                />
                              ) : (
                                <img
                                  src={`${process.env.PUBLIC_URL}/images/icons/add-to-cart.svg`}
                                  alt="Add to Cart"
                                  width={20}
                                  height={20}
                                />
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              );
            })}
          </div>

          {totalItems > 1 && (
            <>
              <button
                className={styles["nav-arrow"] + " " + styles["nav-arrow-left"]}
                onClick={goToPrevious}
                aria-label="Previous slide"
              >
                <img
                  src={`${process.env.PUBLIC_URL}/images/logos/arrow-left.svg`}
                  alt="Previous"
                  height="20"
                />
              </button>
              <button
                className={styles["nav-arrow"] + " " + styles["nav-arrow-right"]}
                onClick={goToNext}
                aria-label="Next slide"
              >
                <img
                  src={`${process.env.PUBLIC_URL}/images/logos/arrow-right.svg`}
                  alt="Next"
                  height="20"
                />
              </button>
            </>
          )}

          {totalItems > 1 && (
            <div className={styles["dots-indicator"]}>
              {items.map((_, index) => (
                <button
                  key={index}
                  className={`${styles["dot"]} ${
                    index === currentIndex ? styles["dot-active"] : ""
                  }`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeroBannerSlider;

